<script context="module">
  // @ts-check

  /**
   * Array of valid aspect ratios for the Image component.
   * @type {("portrait" | "landscape" | "square")[]}
   */
  export const VALID_ASPECT_RATIOS = ["portrait", "landscape", "square"];
</script>

<script>
  // @ts-check
  import { onMount, onDestroy, getContext } from "svelte";

  import Image from "../atoms/Image.svelte";
  import Markdown from "../atoms/Markdown.svelte";

  /** @type { string } */
  let className = "";
  export { className as class };

  /**
   * Primarily used for testing
   * @type {string}
   */
  export let id = null;

  /**
   * @type {import('../types/shared.js').Image}
   */
  export let image = null;

  /**
   * The caption for the image
   * @type {string}
   */
  export let caption = null;

  /**
   * The index of the image within the grid.
   * @type {number}
   */
  export let imageGridIndex = 0;

  /**
   * The external class for the ImageContent element.
   * @type {string}
   */
  export let containerClasses = "";

  /**
   * The aspect ratio of the image used to generate aspect ratio classes.
   * @type {typeof VALID_ASPECT_RATIOS[number]}
   */
  export let aspectRatio = "landscape";

  /**
   * Controls whether the caption should have padding on the left and right
   * @type { boolean }
   */
  export let isCaptionFullBleed = false;

  const captionWrapperClasses = isCaptionFullBleed ? "px-7.5 lg:px-15" : "";

  const imageAspectRatios = {
    square: "aspect-1/1",
    portrait: "aspect-3/4",
    landscape: "aspect-3/2"
  };

  // Get the content type context to determine if the image should have a max height.
  // This is used to prevent images from overflowing the viewport in certain content types.
  const parentContentTypeContext = getContext("parentContentTypeContext");
  const contentType = parentContentTypeContext?.contentType;
  const associatedContentTypesImageShouldHaveMaxHeight = [
    "imageComponent",
    "layout",
    "section",
    "imageGroup"
  ];

  /**
   * @type {string}
   * @description The height classes for the image container.
   *
   * This class sets the maximum height for a image to be the height of the viewport minus the height of the navigation bar so that it does not overflow the viewport.
   */
  let imageHeightClasses =
    isCaptionFullBleed || !associatedContentTypesImageShouldHaveMaxHeight.includes(contentType)
      ? ""
      : "max-h-[calc(100vh-var(--nav-height))]";

  let imageContainerRef;
  let figcaptionLeftMargin = "0px";
  let figcaptionRightMargin = "0px";

  // Determine the margin class for the image container based on the imageGridIndex.
  // If it's even, align to the left, and if it's odd, align to the right.
  let imageContainerMarginClass =
    imageGridIndex === 0 ? "mx-auto" : imageGridIndex % 2 === 0 ? "mr-auto" : "ml-auto";

  function updateCaptionPosition() {
    if (imageContainerRef) {
      const style = getComputedStyle(imageContainerRef);
      figcaptionLeftMargin = style.marginLeft;
      figcaptionRightMargin = style.marginRight;
    }
  }

  let resizeObserver;
  onDestroy(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });
  onMount(() => {
    updateCaptionPosition();

    resizeObserver = new ResizeObserver(() => {
      updateCaptionPosition();
    });
    if (imageContainerRef) {
      resizeObserver.observe(imageContainerRef);
    }
  });
</script>

<figure class="w-full {containerClasses} {className}" {id}>
  <Image
    bind:imageContainerRef
    {...image}
    containerElement="div"
    aspectRatioClasses={imageAspectRatios[aspectRatio]
      ? imageAspectRatios[aspectRatio]
      : aspectRatio}
    containerClass="{imageContainerMarginClass} {imageHeightClasses}"
  />
  {#if caption || $$slots.caption}
    <figcaption
      class="body-sm mt-3 {captionWrapperClasses}"
      style="
      margin-left: {figcaptionLeftMargin};
      margin-right: {figcaptionRightMargin};
      text-align: left;"
    >
      <slot name="caption">
        <Markdown element="span" input={caption} id="markdown-caption-${id}" />
      </slot>
    </figcaption>
  {/if}
</figure>
